import { GridFrame } from 'Common/Components/GridFrame'
import { Box, IconButton, Tooltip } from '@mui/material'
import {
  InventorySummary,
  safetyStockWarning,
} from 'Features/Projects/Scenarios/Inventory/Inventory.types'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { useMemo, useState } from 'react'
import { ActionItem } from 'Common/Components/MUITableAction'
import {
  InventoryPayloadType,
  useCalculateSafetyStock,
} from 'Features/Projects/Scenarios/Inventory/Inventory.data'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from 'Common/Supplyvue-ui'
import { Table } from 'Common/MUITableV2'
import RefreshIcon from '@mui/icons-material/Refresh'
import CalculateOutlined from '@mui/icons-material/CalculateOutlined'
import { RunStatusChip } from 'Common/Components/RunStatusChip'
import { GridActionBar, JOINED_GRID_SX } from 'Common/Components/GridActionBar'
import { Status } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'

export const InventorySummaryTable = ({
  data,
  isRefetching,
  handleRefetch,
}: {
  data: InventorySummary[]
  isRefetching: boolean
  handleRefetch: () => void
}) => {
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>() as { projectId: string; scenarioId: string }

  const navigate = useNavigate()

  const { mutate: calculateSafetyStock, isPending: isCalculating } =
    useCalculateSafetyStock(
      () => {
        toast.success('Success')
      },
      () => {
        toast.error('Error')
      }
    )

  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const handleRowSelectionChange = (
    rowSelectionModel: GridRowSelectionModel
  ) => {
    setSelectedRows([...rowSelectionModel.ids])
  }

  const handleCalculateSafetyStock = (selectedRows: GridRowId[]) => {
    const payload: InventoryPayloadType[] = data
      .filter((setting: InventorySummary) => selectedRows.includes(setting.id))
      .map((setting: InventorySummary) => ({
        siteKey: setting.siteKey,
      }))
    calculateSafetyStock({ projectId, scenarioId, payload })
  }

  const getStatusIcon = (status: Status) => <RunStatusChip status={status} />

  const COLUMN_WIDTH = 150

  const handleViewResults = (params: GridRowParams<InventorySummary>): void => {
    const { siteKey } = params.row
    navigate(
      `/projects/${projectId}/scenarios/${scenarioId}/scenario/inventory-results/${siteKey}`
    )
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'siteCode',
        headerName: 'Site',
        type: 'string',
        width: COLUMN_WIDTH,
        flex: 1,
      },
      {
        field: 'skuGroupCount',
        headerName: 'Product Count',
        type: 'number',
        width: COLUMN_WIDTH,
        flex: 1,
      },
      {
        field: 'safetyStockStatus',
        headerName: 'Status',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<InventorySummary>) => {
          const warning = safetyStockWarning(params.row)
          return (
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {getStatusIcon(params.value)}
              {warning && (
                <Tooltip title={warning}>
                  <WarningAmberOutlined
                    color="warning"
                    sx={{ fontSize: 16 }}
                    aria-label="Out of date"
                  />
                </Tooltip>
              )}
            </Box>
          )
        },
        type: 'string',
        width: 150,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        getActions: (params: GridRowParams<InventorySummary>) => {
          const { safetyStockStatus } = params.row
          return [
            <ActionItem
              key="view"
              showInMenu
              label="Results"
              disabled={safetyStockStatus !== 'completed'}
              clickHandler={() => {
                handleViewResults(params)
              }}
            />,
          ]
        },
      },
    ],
    [data]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <GridActionBar
        hint={
          selectedRows.length
            ? `${selectedRows.length} site${selectedRows.length === 1 ? '' : 's'} selected`
            : 'Select sites to calculate their safety stock'
        }
      >
        <Tooltip title="Refresh">
          <IconButton size="small" aria-label="Refresh" onClick={handleRefetch}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          disabled={selectedRows.length === 0 || isCalculating}
          loading={isCalculating}
          loadingPosition="start"
          startIcon={<CalculateOutlined />}
          onClick={() => handleCalculateSafetyStock(selectedRows)}
        >
          Calculate safety stock
        </Button>
      </GridActionBar>
      <GridFrame>
        <Table
          rows={data}
          columns={columns}
          checkboxSelection={true}
          onRowSelectionModelChange={handleRowSelectionChange}
          showCellVerticalBorder
          showColumnVerticalBorder
          loading={isRefetching}
          sx={{ ...JOINED_GRID_SX, height: '100%' }}
        />
      </GridFrame>
    </Box>
  )
}
